import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  fetchDistributors,
  searchPlatformUserForDistributor,
  createDistributor,
  updateDistributor,
} from '@/lib/distributors'
import { ApiClientError } from '@/lib/api/client'
import {
  applyDoeyUserIdAfterUserSearch,
  canSubmitCredentialFormWithDoeyPayout,
  DOEY_GATEWAY_REQUIRED_MESSAGE,
} from '@/lib/institution-doey-payout'
import { useInstitutionCampaignSelect } from '@/hooks/useInstitutionCampaignSelect'
import type { Campaign } from '@/lib/campaigns'
import type {
  Distributor,
  DistributorPayoutMethod,
  CreateDistributorInput,
  PlatformUser,
  UpdateDistributorInput,
} from '@/types/distributors'
import styles from './DistribuidoresListaPage.module.css'

function formatCurrency(value: number) {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function filterActiveCampaignsWithSlug(campaigns: Campaign[]): Campaign[] {
  return campaigns.filter(
    (c) => c.status === 'active' && typeof c.slug === 'string' && c.slug.trim() !== ''
  )
}

function parseCommissionPercent(value: string): number | null {
  const num = parseFloat(value.replace(',', '.'))
  if (Number.isNaN(num) || num < 0.1 || num > 80) {
    return null
  }
  return num
}

interface DistributorModalProps {
  distributor: Distributor | null
  activeCampaigns: Campaign[]
  isSubmitting: boolean
  onClose: () => void
  onSubmit: (data: CreateDistributorInput | UpdateDistributorInput, id?: number) => Promise<void>
}

function DistributorModal({
  distributor,
  activeCampaigns,
  isSubmitting,
  onClose,
  onSubmit,
}: DistributorModalProps) {
  const isEdit = distributor !== null

  const [searchEmail, setSearchEmail] = useState('')
  const [searchPhone, setSearchPhone] = useState('')
  const [selectedUser, setSelectedUser] = useState<PlatformUser | null>(null)
  const [isSearchingUser, setIsSearchingUser] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [payoutMethod, setPayoutMethod] = useState<DistributorPayoutMethod>(
    distributor?.payout_method ?? 'doey'
  )
  const [doeyUserId, setDoeyUserId] = useState(distributor?.doey_user_id ?? '')
  const [commissionPercent, setCommissionPercent] = useState(
    distributor ? String(distributor.commission_percent) : ''
  )
  const [maxAffiliateCommissionPercent, setMaxAffiliateCommissionPercent] = useState(
    distributor ? String(distributor.max_affiliate_commission_percent) : ''
  )
  const [isActive, setIsActive] = useState(distributor?.is_active ?? true)
  const [allCampaigns, setAllCampaigns] = useState(distributor?.all_campaigns ?? true)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<number[]>(
    () => distributor?.allowed_campaigns.map((c) => c.id) ?? []
  )
  const [campaignCommissions, setCampaignCommissions] = useState<Record<number, string>>(() => {
    const initial: Record<number, string> = {}
    for (const c of distributor?.allowed_campaigns ?? []) {
      if (c.commission_percent !== undefined) {
        initial[c.id] = String(c.commission_percent)
      }
    }
    return initial
  })
  const [campaignMaxCommissions, setCampaignMaxCommissions] = useState<Record<number, string>>(
    () => {
      const initial: Record<number, string> = {}
      for (const c of distributor?.allowed_campaigns ?? []) {
        if (c.max_affiliate_commission_percent !== undefined) {
          initial[c.id] = String(c.max_affiliate_commission_percent)
        }
      }
      return initial
    }
  )
  const [formError, setFormError] = useState<string | null>(null)
  const [gatewayAlert, setGatewayAlert] = useState<string | null>(null)

  useEffect(() => {
    const sync = window.setTimeout(() => {
      if (distributor) {
        setPayoutMethod(distributor.payout_method)
        setDoeyUserId(
          distributor.payout_method === 'doey'
            ? distributor.gateway_doey_user_id ?? distributor.doey_user_id ?? ''
            : ''
        )
        setCommissionPercent(String(distributor.commission_percent))
        setMaxAffiliateCommissionPercent(String(distributor.max_affiliate_commission_percent))
        setIsActive(distributor.is_active)
        setAllCampaigns(distributor.all_campaigns ?? true)
        setSelectedCampaignIds((distributor.allowed_campaigns ?? []).map((c) => c.id))
        const commissions: Record<number, string> = {}
        const maxCommissions: Record<number, string> = {}
        for (const c of distributor.allowed_campaigns ?? []) {
          if (c.commission_percent !== undefined) {
            commissions[c.id] = String(c.commission_percent)
          }
          if (c.max_affiliate_commission_percent !== undefined) {
            maxCommissions[c.id] = String(c.max_affiliate_commission_percent)
          }
        }
        setCampaignCommissions(commissions)
        setCampaignMaxCommissions(maxCommissions)
      } else {
        setPayoutMethod('doey')
        setDoeyUserId('')
        setCommissionPercent('')
        setMaxAffiliateCommissionPercent('')
        setIsActive(true)
        setAllCampaigns(true)
        setSelectedCampaignIds([])
        setCampaignCommissions({})
        setCampaignMaxCommissions({})
        setSelectedUser(null)
        setSearchEmail('')
        setSearchPhone('')
        setSearchError(null)
        setGatewayAlert(null)
      }
    }, 0)
    return () => window.clearTimeout(sync)
  }, [distributor])

  useEffect(() => {
    if (payoutMethod !== 'doey') {
      setDoeyUserId('')
      setGatewayAlert(null)
      return
    }
    if (selectedUser) {
      const doeyApply = applyDoeyUserIdAfterUserSearch(payoutMethod, selectedUser.doey_user_id)
      setDoeyUserId(doeyApply.ok ? doeyApply.doeyUserId : '')
      setGatewayAlert(doeyApply.ok ? null : doeyApply.gatewayError)
      return
    }
    setDoeyUserId(
      distributor ? distributor.gateway_doey_user_id ?? distributor.doey_user_id ?? '' : ''
    )
    setGatewayAlert(null)
  }, [distributor, isEdit, payoutMethod, selectedUser])

  const handleSearchUser = async () => {
    const email = searchEmail.trim()
    const phone = searchPhone.replace(/\D/g, '')
    if (!email && !phone) {
      setSearchError('Informe o e-mail ou o telefone para procurar.')
      setSelectedUser(null)
      return
    }
    setIsSearchingUser(true)
    setSearchError(null)
    try {
      const res = await searchPlatformUserForDistributor({
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
      })
      setSelectedUser(res.user)
      const doeyApply = applyDoeyUserIdAfterUserSearch(payoutMethod, res.user.doey_user_id)
      if (!doeyApply.ok) {
        setGatewayAlert(doeyApply.gatewayError)
        setDoeyUserId('')
      } else {
        setGatewayAlert(null)
        if (payoutMethod === 'doey') {
          setDoeyUserId(doeyApply.doeyUserId)
        }
      }
    } catch (err) {
      setSelectedUser(null)
      setGatewayAlert(null)
      if (err instanceof ApiClientError) {
        setSearchError(err.message)
      } else {
        setSearchError('Usuário não encontrado.')
      }
    } finally {
      setIsSearchingUser(false)
    }
  }

  const globalCommissionNum = parseCommissionPercent(commissionPercent)
  const globalMaxAffiliateNum = parseCommissionPercent(maxAffiliateCommissionPercent)
  const isGlobalCommissionValid = globalCommissionNum !== null
  const isGlobalMaxAffiliateValid = globalMaxAffiliateNum !== null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFormError(null)

    if (!isEdit && !selectedUser) {
      setFormError('Procure e selecione um usuário da plataforma.')
      return
    }
    if (
      !canSubmitCredentialFormWithDoeyPayout(
        isEdit,
        payoutMethod,
        selectedUser?.doey_user_id ?? doeyUserId,
        distributor?.payout_method,
        distributor?.doey_user_id
      )
    ) {
      setFormError(DOEY_GATEWAY_REQUIRED_MESSAGE)
      return
    }
    if (allCampaigns) {
      if (!isGlobalCommissionValid || !isGlobalMaxAffiliateValid) {
        setFormError('As comissões devem ser entre 0,1% e 80%.')
        return
      }
    } else {
      if (selectedCampaignIds.length === 0) {
        setFormError('Selecione ao menos uma campanha ou marque Todas as Campanhas.')
        return
      }
      for (const campaignId of selectedCampaignIds) {
        const pct = parseCommissionPercent(campaignCommissions[campaignId] ?? '')
        const maxPct = parseCommissionPercent(campaignMaxCommissions[campaignId] ?? '')
        if (pct === null || maxPct === null) {
          setFormError(
            'Informe a comissão do distribuidor e o teto do afiliado (0,1% a 80%) para cada campanha selecionada.'
          )
          return
        }
      }
    }

    const campaignsPayload = allCampaigns
      ? undefined
      : selectedCampaignIds.map((campaignId) => ({
          campaign_id: campaignId,
          commission_percent: parseCommissionPercent(campaignCommissions[campaignId] ?? '')!,
          max_affiliate_commission_percent: parseCommissionPercent(
            campaignMaxCommissions[campaignId] ?? ''
          )!,
        }))

    if (isEdit) {
      const updateData: UpdateDistributorInput = {
        is_active: isActive,
        payout_method: payoutMethod,
        all_campaigns: allCampaigns,
        ...(allCampaigns && globalCommissionNum !== null
          ? { commission_percent: globalCommissionNum }
          : {}),
        ...(allCampaigns && globalMaxAffiliateNum !== null
          ? { max_affiliate_commission_percent: globalMaxAffiliateNum }
          : {}),
        ...(campaignsPayload ? { campaigns: campaignsPayload } : {}),
      }
      await onSubmit(updateData, distributor.id)
    } else {
      const createData: CreateDistributorInput = {
        platform_user_id: selectedUser!.id,
        payout_method: payoutMethod,
        all_campaigns: allCampaigns,
        ...(allCampaigns && globalCommissionNum !== null
          ? { commission_percent: globalCommissionNum }
          : {}),
        ...(allCampaigns && globalMaxAffiliateNum !== null
          ? { max_affiliate_commission_percent: globalMaxAffiliateNum }
          : {}),
        ...(campaignsPayload ? { campaigns: campaignsPayload } : {}),
      }
      await onSubmit(createData)
    }
  }

  const toggleCampaignId = (campaignId: number) => {
    setSelectedCampaignIds((prev) => {
      if (prev.includes(campaignId)) {
        setCampaignCommissions((commissions) => {
          const next = { ...commissions }
          delete next[campaignId]
          return next
        })
        setCampaignMaxCommissions((maxCommissions) => {
          const next = { ...maxCommissions }
          delete next[campaignId]
          return next
        })
        return prev.filter((id) => id !== campaignId)
      }
      return [...prev, campaignId]
    })
  }

  return (
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="distributor-modal-title"
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h3 id="distributor-modal-title" className={styles.modalTitle}>
            {isEdit ? 'Editar Distribuidor' : 'Novo Distribuidor'}
          </h3>
          <button className={styles.modalClose} onClick={onClose} aria-label="Fechar">
            <Icon icon="mdi:close" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)}>
          <div className={styles.modalBody}>
            {!isEdit && (
              <div className={styles.field}>
                <span className={styles.label}>Usuário da plataforma *</span>
                <p className={styles.searchHint}>
                  Informe o e-mail ou o telefone do cadastro e clique em Procurar.
                </p>
                <div className={styles.userSearchRow}>
                  <input
                    className={styles.input}
                    type="email"
                    placeholder="E-mail"
                    value={searchEmail}
                    onChange={(e) => {
                      setSearchEmail(e.target.value)
                      setSearchError(null)
                    }}
                    autoComplete="off"
                  />
                  <input
                    className={styles.input}
                    type="tel"
                    placeholder="Telefone"
                    value={searchPhone}
                    onChange={(e) => {
                      setSearchPhone(e.target.value)
                      setSearchError(null)
                    }}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className={styles.searchUserBtn}
                    disabled={isSearchingUser}
                    onClick={() => void handleSearchUser()}
                  >
                    {isSearchingUser ? 'Procurando…' : 'Procurar'}
                  </button>
                </div>
                {searchError ? (
                  <p className={styles.searchError} role="alert">
                    {searchError}
                  </p>
                ) : null}
                {selectedUser ? (
                  <div className={styles.selectedDonor}>
                    <span>
                      {selectedUser.name}
                      {selectedUser.email ? ` — ${selectedUser.email}` : ''}
                      {selectedUser.phone ? ` — ${selectedUser.phone}` : ''}
                    </span>
                    <button
                      type="button"
                      className={styles.clearDonor}
                      onClick={() => {
                        setSelectedUser(null)
                        setSearchError(null)
                      }}
                    >
                      <Icon icon="mdi:close-circle" />
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            {isEdit && (
              <div className={styles.field}>
                <label className={styles.label}>Distribuidor</label>
                <div className={styles.readonlyField}>{distributor.distributor_name}</div>
              </div>
            )}

            <div className={styles.field}>
              <span className={styles.label} id="distributor-payout-label">
                Pagamento da comissão *
              </span>
              <div
                className={styles.payoutGrid}
                role="radiogroup"
                aria-labelledby="distributor-payout-label"
              >
                <label
                  className={`${styles.payoutCard} ${payoutMethod === 'doey' ? styles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="payout"
                    className={styles.payoutInputHidden}
                    checked={payoutMethod === 'doey'}
                    onChange={() => setPayoutMethod('doey')}
                  />
                  <span className={styles.payoutCardInner}>
                    <span className={`${styles.payoutIconWrap} ${styles.payoutIconDoey}`}>
                      <Icon icon="mdi:flash" aria-hidden />
                    </span>
                    <span className={styles.payoutTexts}>
                      <span className={styles.payoutTitle}>Doey automático</span>
                      <span className={styles.payoutSub}>Split no PIX</span>
                    </span>
                  </span>
                </label>
                <label
                  className={`${styles.payoutCard} ${payoutMethod === 'manual' ? styles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="payout"
                    className={styles.payoutInputHidden}
                    checked={payoutMethod === 'manual'}
                    onChange={() => setPayoutMethod('manual')}
                  />
                  <span className={styles.payoutCardInner}>
                    <span className={`${styles.payoutIconWrap} ${styles.payoutIconManual}`}>
                      <Icon icon="mdi:wallet-outline" aria-hidden />
                    </span>
                    <span className={styles.payoutTexts}>
                      <span className={styles.payoutTitle}>Manual</span>
                      <span className={styles.payoutSub}>Saldo interno</span>
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {payoutMethod === 'doey' && (
              <div className={styles.field}>
                <label className={styles.label} htmlFor="doey-user-id">
                  ID Doey *
                </label>
                <input
                  id="doey-user-id"
                  className={styles.input}
                  placeholder="Preenchido pelo Gateway de Pagamento"
                  maxLength={36}
                  value={doeyUserId}
                  readOnly
                  disabled
                  aria-readonly="true"
                />
                <p className={styles.fieldHint}>
                  Preenchido automaticamente pelo Gateway de Pagamento do usuário pesquisado.
                </p>
                {gatewayAlert && (
                  <div className={styles.formError} role="alert">
                    <Icon icon="mdi:alert-circle-outline" aria-hidden />
                    {gatewayAlert}
                  </div>
                )}
              </div>
            )}

            <div className={styles.field}>
              <span className={styles.label} id="distributor-campaigns-label">
                Campanhas autorizadas *
              </span>
              <div
                className={styles.payoutGrid}
                role="radiogroup"
                aria-labelledby="distributor-campaigns-label"
              >
                <label
                  className={`${styles.payoutCard} ${allCampaigns ? styles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="campaign-access"
                    className={styles.payoutInputHidden}
                    checked={allCampaigns}
                    onChange={() => {
                      setAllCampaigns(true)
                      setSelectedCampaignIds([])
                    }}
                  />
                  <span className={styles.payoutCardInner}>
                    <span className={`${styles.payoutIconWrap} ${styles.campaignIconAll}`}>
                      <Icon icon="mdi:bullhorn-outline" aria-hidden />
                    </span>
                    <span className={styles.payoutTexts}>
                      <span className={styles.payoutTitle}>Todas as Campanhas</span>
                      <span className={styles.payoutSub}>Qualquer campanha ativa da instituição</span>
                    </span>
                  </span>
                </label>
                <label
                  className={`${styles.payoutCard} ${!allCampaigns ? styles.payoutCardActive : ''}`}
                >
                  <input
                    type="radio"
                    name="campaign-access"
                    className={styles.payoutInputHidden}
                    checked={!allCampaigns}
                    onChange={() => setAllCampaigns(false)}
                  />
                  <span className={styles.payoutCardInner}>
                    <span className={`${styles.payoutIconWrap} ${styles.campaignIconSpecific}`}>
                      <Icon icon="mdi:format-list-checks" aria-hidden />
                    </span>
                    <span className={styles.payoutTexts}>
                      <span className={styles.payoutTitle}>Campanhas específicas</span>
                      <span className={styles.payoutSub}>Selecione abaixo as permitidas</span>
                    </span>
                  </span>
                </label>
              </div>
              {!allCampaigns && (
                <div className={styles.campaignPickPanel}>
                  {activeCampaigns.length === 0 ? (
                    <p className={styles.campaignPickEmpty}>
                      Nenhuma campanha ativa com slug cadastrado. Ative uma campanha antes de
                      restringir o distribuidor.
                    </p>
                  ) : (
                    <>
                      <p className={styles.campaignPickHint}>
                        Marque as campanhas que este distribuidor pode operar:
                      </p>
                      <div className={styles.campaignPickGrid}>
                        {activeCampaigns.map((campaign) => {
                          const isSelected = selectedCampaignIds.includes(campaign.id)
                          const label = campaign.title?.trim() || campaign.name
                          return (
                            <div
                              key={campaign.id}
                              className={`${styles.campaignPickCard} ${isSelected ? styles.campaignPickCardActive : ''}`}
                            >
                              <label className={styles.campaignPickCardLabel}>
                                <input
                                  type="checkbox"
                                  className={styles.payoutInputHidden}
                                  checked={isSelected}
                                  onChange={() => toggleCampaignId(campaign.id)}
                                />
                                <span className={styles.campaignPickCardInner}>
                                  <Icon
                                    icon={
                                      isSelected
                                        ? 'mdi:checkbox-marked-circle'
                                        : 'mdi:checkbox-blank-circle-outline'
                                    }
                                    className={styles.campaignPickCheckIcon}
                                    aria-hidden
                                  />
                                  <span className={styles.campaignPickName}>{label}</span>
                                </span>
                              </label>
                              {isSelected && (
                                <div className={styles.campaignPickCommission}>
                                  <label
                                    className={styles.campaignPickCommissionLabel}
                                    htmlFor={`campaign-commission-${campaign.id}`}
                                  >
                                    Comissão distribuidor (%)
                                  </label>
                                  <input
                                    id={`campaign-commission-${campaign.id}`}
                                    className={styles.campaignPickCommissionInput}
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    max="80"
                                    placeholder="10"
                                    value={campaignCommissions[campaign.id] ?? ''}
                                    onChange={(e) =>
                                      setCampaignCommissions((prev) => ({
                                        ...prev,
                                        [campaign.id]: e.target.value,
                                      }))
                                    }
                                  />
                                  <label
                                    className={styles.campaignPickCommissionLabel}
                                    htmlFor={`campaign-max-affiliate-${campaign.id}`}
                                  >
                                    Teto afiliado (%)
                                  </label>
                                  <input
                                    id={`campaign-max-affiliate-${campaign.id}`}
                                    className={styles.campaignPickCommissionInput}
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    max="80"
                                    placeholder="5"
                                    value={campaignMaxCommissions[campaign.id] ?? ''}
                                    onChange={(e) =>
                                      setCampaignMaxCommissions((prev) => ({
                                        ...prev,
                                        [campaign.id]: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            {allCampaigns && (
              <>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="commission">
                    Comissão do distribuidor (%) *
                  </label>
                  <input
                    id="commission"
                    className={styles.input}
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="80"
                    placeholder="10"
                    value={commissionPercent}
                    onChange={(e) => setCommissionPercent(e.target.value)}
                  />
                  <span className={styles.fieldHint}>0,1% a 80% — vale para todas as campanhas</span>
                </div>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="max-affiliate-commission">
                    Teto de comissão do afiliado (%) *
                  </label>
                  <input
                    id="max-affiliate-commission"
                    className={styles.input}
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="80"
                    placeholder="5"
                    value={maxAffiliateCommissionPercent}
                    onChange={(e) => setMaxAffiliateCommissionPercent(e.target.value)}
                  />
                  <span className={styles.fieldHint}>
                    Máximo que afiliados deste distribuidor podem receber por venda
                  </span>
                </div>
              </>
            )}

            {isEdit && (
              <div className={styles.field}>
                <label className={styles.toggleLabel}>
                  <span>Distribuidor ativo</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isActive}
                    className={`${styles.toggle} ${isActive ? styles.toggleOn : ''}`}
                    onClick={() => setIsActive((v) => !v)}
                  >
                    <span className={styles.toggleThumb} />
                  </button>
                </label>
              </div>
            )}

            {formError && (
              <div className={styles.formError}>
                <Icon icon="mdi:alert-circle-outline" />
                {formError}
              </div>
            )}
          </div>

          <div className={styles.modalFooter}>
            <button type="button" className={styles.btnCancel} onClick={onClose}>
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.btnSubmit}
              disabled={
                isSubmitting ||
                !canSubmitCredentialFormWithDoeyPayout(
                  isEdit,
                  payoutMethod,
                  selectedUser?.doey_user_id ?? doeyUserId,
                  distributor?.payout_method,
                  distributor?.doey_user_id
                )
              }
            >
              {isSubmitting ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar Distribuidor'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function DistribuidoresListaPage() {
  const { campaigns } = useInstitutionCampaignSelect()
  const activeCampaigns = useMemo(
    () => filterActiveCampaignsWithSlug(campaigns),
    [campaigns]
  )

  const [distributors, setDistributors] = useState<Distributor[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingDistributor, setEditingDistributor] = useState<Distributor | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchDistributors()
      setDistributors(res.distributors)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar distribuidores.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = distributors.filter(
    (d) =>
      d.distributor_name.toLowerCase().includes(search.toLowerCase()) ||
      String(d.id).includes(search)
  )

  const handleModalSubmit = async (
    data: CreateDistributorInput | UpdateDistributorInput,
    id?: number
  ) => {
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      if (id != null) {
        await updateDistributor(id, data as UpdateDistributorInput)
      } else {
        await createDistributor(data as CreateDistributorInput)
      }
      setIsModalOpen(false)
      setEditingDistributor(null)
      await load()
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Erro ao salvar distribuidor.'
      setSubmitError(msg)
      throw err
    } finally {
      setIsSubmitting(false)
    }
  }

  const openCreate = () => {
    setEditingDistributor(null)
    setSubmitError(null)
    setIsModalOpen(true)
  }

  const openEdit = (distributor: Distributor) => {
    setEditingDistributor(distributor)
    setSubmitError(null)
    setIsModalOpen(true)
  }

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <Icon icon="mdi:magnify" className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Buscar distribuidor..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button className={styles.btnNew} onClick={openCreate}>
          <Icon icon="mdi:plus" />
          Novo Distribuidor
        </button>
      </div>

      {submitError && (
        <div className={styles.globalError}>
          <Icon icon="mdi:alert-circle-outline" />
          {submitError}
        </div>
      )}

      {isLoading ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
          Carregando...
        </div>
      ) : error ? (
        <div className={styles.centered}>
          <Icon icon="mdi:alert-circle-outline" className={styles.errorIcon} />
          {error}
          <button className={styles.retryBtn} onClick={() => void load()}>
            Tentar novamente
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className={styles.centered}>
          <Icon icon="mdi:account-group-outline" className={styles.emptyIcon} />
          {search
            ? 'Nenhum distribuidor encontrado para a busca.'
            : 'Nenhum distribuidor ativo. Crie o primeiro!'}
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th># ID</th>
                <th>Nome</th>
                <th>Afiliados</th>
                <th>Vendas</th>
                <th>Pagamento</th>
                <th>Saldo interno</th>
                <th>Comissão total</th>
                <th>% Distribuidor</th>
                <th>Teto afiliado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((distributor) => (
                <tr key={distributor.id}>
                  <td className={styles.idCell}>{distributor.id}</td>
                  <td>
                    <span className={styles.affiliateName}>{distributor.distributor_name}</span>
                  </td>
                  <td>
                    <span className={styles.badge}>
                      {distributor.registrations_count} afiliado
                      {distributor.registrations_count === 1 ? '' : 's'}
                    </span>
                  </td>
                  <td>
                    <span className={styles.badge}>
                      {distributor.metrics.payments_count} vendas
                    </span>
                  </td>
                  <td>
                    <span className={styles.payoutBadge}>
                      {distributor.payout_method === 'doey' ? 'Doey (auto)' : 'Manual'}
                    </span>
                  </td>
                  <td className={styles.amountCell}>
                    {distributor.payout_method === 'manual'
                      ? formatCurrency(distributor.balance)
                      : '—'}
                  </td>
                  <td className={styles.amountCell}>
                    {formatCurrency(distributor.metrics.total_commission)}
                  </td>
                  <td className={styles.commissionCell}>
                    {distributor.all_campaigns
                      ? `${distributor.commission_percent}%`
                      : 'Por campanha'}
                  </td>
                  <td className={styles.commissionCell}>
                    {distributor.all_campaigns
                      ? `${distributor.max_affiliate_commission_percent}%`
                      : 'Por campanha'}
                  </td>
                  <td>
                    <div className={styles.actionCell}>
                      {distributor.payout_method === 'manual' && (
                        <Link
                          className={styles.saldoLink}
                          to={`/meus-distribuidores/saldo?distributor_id=${distributor.id}`}
                        >
                          <Icon icon="mdi:wallet-outline" />
                          Saldo
                        </Link>
                      )}
                      <button
                        className={styles.configBtn}
                        onClick={() => openEdit(distributor)}
                      >
                        <Icon icon="mdi:cog-outline" />
                        Config
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className={styles.tableFooter}>
            Mostrando {filtered.length} de {distributors.length} distribuidores
          </div>
        </div>
      )}

      {isModalOpen &&
        createPortal(
          <DistributorModal
            distributor={editingDistributor}
            activeCampaigns={activeCampaigns}
            isSubmitting={isSubmitting}
            onClose={() => {
              setIsModalOpen(false)
              setEditingDistributor(null)
            }}
            onSubmit={handleModalSubmit}
          />,
          document.body
        )}
    </div>
  )
}
